import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Autocomplete, Box, Button, Chip, Divider, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useConfirm } from 'material-ui-confirm';

import { noWheelInputProps } from 'util/utils';
import MenuVisibility from 'views/MenuCategory/components/MenuVisibility';
import {
  KITCHEN_FLAGS,
  editedCategoryPayload,
  itemsWithCategoryPromotionTags,
  itemsWithKitchenFlags,
  itemsWithPatch,
  itemsWithoutPromotionTags,
  joinPrintCategories,
  newCategoryPayload,
} from '../menuEditorPayloads';
import EditorDrawer from './EditorDrawer';
import ColorField from './ColorField';

const TABS = [
  { value: 'details', label: 'Details' },
  { value: 'visibility', label: 'Visibility' },
  { value: 'dishes', label: 'All dishes' },
];

const formFrom = (category) => ({
  name: (category && category.name) || '',
  altName: (category && category.altName) || '',
  description: (category && category.description) || '',
  cookingPriority: category && category.cookingPriority != null ? String(category.cookingPriority) : '1',
  hideForWeb: !!(category && category.hideForWeb),
  isHiddenForPickup: !!(category && category.isHiddenForPickup),
  colorHex: (category && category.colorHex) || null,
});

const formErrors = (form) => {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = 'Name is required';
  }
  if (form.cookingPriority === '' || !Number.isInteger(Number(form.cookingPriority))) {
    errors.cookingPriority = 'Enter a whole number';
  }
  return errors;
};

const BulkSection = ({ title, description, children }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {description}
    </Typography>
    {children}
  </Box>
);

BulkSection.propTypes = { title: PropTypes.string.isRequired, description: PropTypes.string, children: PropTypes.node };

/** "All dishes": settings the classic editor's category Options and Settings dialogs write onto every dish. */
const AllDishesTab = ({ category, menuId, menu, printCategoryNames, actions }) => {
  const confirm = useConfirm();
  const items = category.items || [];
  const [busy, setBusy] = useState(false);
  const [promotionTags, setPromotionTags] = useState(category.promotionTags || []);
  const [printCategories, setPrintCategories] = useState([]);
  const seededFlags = useMemo(
    () => Object.fromEntries(KITCHEN_FLAGS.map((flag) => [flag.key, items.length > 0 && items.every((item) => !!item[flag.key])])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category.id]
  );
  const [flags, setFlags] = useState(seededFlags);

  useEffect(() => {
    setPromotionTags(category.promotionTags || []);
    setPrintCategories([]);
    setFlags(seededFlags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.id]);

  const apply = async (description, work) => {
    try {
      await confirm({
        title: `Update all ${items.length} dishes?`,
        description: `${description} This applies to every dish in “${category.name}”.`,
        confirmationText: 'Apply',
      });
    } catch (cancelled) {
      return;
    }
    setBusy(true);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return <Alert severity="info">Add dishes to this category to change them all at once.</Alert>;
  }

  return (
    <Stack spacing={4} divider={<Divider flexItem />}>
      <BulkSection title="Promotion tags" description="Replaces this category's promotion tags on every dish. Tags a dish has of its own are kept.">
        <Autocomplete
          multiple
          freeSolo
          options={(menu && menu.promotionTags) || []}
          value={promotionTags}
          onChange={(event, value) => setPromotionTags([...new Set(value.map((tag) => tag.trim()).filter(Boolean))])}
          renderTags={(value, getTagProps) => value.map((tag, index) => <Chip size="small" label={tag} {...getTagProps({ index })} key={tag} />)}
          renderInput={(params) => <TextField {...params} placeholder="Add a promotion tag" />}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() =>
              apply('Sets these promotion tags.', async () => {
                const saved = await actions.saveItems(itemsWithCategoryPromotionTags(category, promotionTags, menuId));
                if (saved) {
                  await actions.addMenuPromotionTags(promotionTags);
                }
              })
            }
          >
            Apply tags
          </Button>
          <Button color="inherit" disabled={busy} onClick={() => apply('Removes every promotion tag.', () => actions.saveItems(itemsWithoutPromotionTags(category, menuId)))}>
            Remove all promotion tags
          </Button>
        </Box>
      </BulkSection>

      <BulkSection title="Print categories" description="Where every dish in this category prints in the kitchen.">
        {printCategoryNames.length > 0 ? (
          <>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={printCategoryNames}
              value={printCategories}
              onChange={(event, value) => setPrintCategories(value)}
              renderInput={(params) => <TextField {...params} label="Print categories" />}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                disableElevation
                disabled={busy || printCategories.length === 0}
                onClick={() =>
                  apply(`Prints every dish to ${printCategories.join(', ')}.`, () =>
                    actions.saveItems(itemsWithPatch(category, { printCategory: joinPrintCategories(printCategories) }, menuId))
                  )
                }
              >
                Apply print categories
              </Button>
              <Button color="inherit" disabled={busy} onClick={() => apply('Clears the print categories.', () => actions.saveItems(itemsWithPatch(category, { printCategory: null }, menuId)))}>
                Clear from all dishes
              </Button>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This menu has no cooking categories.
          </Typography>
        )}
        <Button
          color="inherit"
          disabled={busy}
          sx={{ mt: 1.5 }}
          onClick={() => apply('Removes the KDS cooking category.', () => actions.saveItems(itemsWithPatch(category, { cookingCategory: null }, menuId)))}
        >
          Remove KDS cooking categories from all dishes
        </Button>
      </BulkSection>

      <BulkSection title="Kitchen settings" description="All four settings are written to every dish together. A switch starts on only when every dish already has it on.">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {KITCHEN_FLAGS.map((flag) => (
            <FormControlLabel
              key={flag.key}
              label={flag.label}
              control={<Switch checked={flags[flag.key]} onChange={(event) => setFlags({ ...flags, [flag.key]: event.target.checked })} />}
            />
          ))}
        </Box>
        <Button
          variant="contained"
          disableElevation
          disabled={busy}
          sx={{ mt: 1 }}
          onClick={() => apply('Sets the kitchen settings shown.', () => actions.saveItems(itemsWithKitchenFlags(category, flags, menuId)))}
        >
          Apply kitchen settings
        </Button>
      </BulkSection>
    </Stack>
  );
};

AllDishesTab.propTypes = {
  category: PropTypes.object.isRequired,
  menuId: PropTypes.string.isRequired,
  menu: PropTypes.object,
  printCategoryNames: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
};

const CategoryEditor = ({ open, mode, category, menuId, menu, printCategoryNames, showIds, actions, onClose, onDeleted }) => {
  const confirm = useConfirm();
  const isNew = mode === 'new';
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState(formFrom(null));
  const [saving, setSaving] = useState(false);
  const categoryId = category ? category.id : null;

  useEffect(() => {
    setForm(formFrom(isNew ? null : category));
    setTab('details');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, isNew, open]);

  const errors = formErrors(form);
  const dirty = JSON.stringify(form) !== JSON.stringify(formFrom(isNew ? null : category));
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const toggle = (key) => (event) => setForm({ ...form, [key]: event.target.checked });

  const close = async () => {
    if (dirty && tab === 'details') {
      try {
        await confirm({ title: 'Discard changes?', description: 'You have changes that have not been saved.', confirmationText: 'Discard' });
      } catch (cancelled) {
        return;
      }
    }
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (await actions.createCategory(newCategoryPayload(form, menuId))) {
          onClose();
        }
      } else {
        await actions.updateCategory(editedCategoryPayload(form, category, menuId));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await confirm({
        title: `Delete “${category.name}”?`,
        description: `This deletes the category and its ${(category.items || []).length} dishes.`,
        confirmationText: 'Delete',
        confirmationButtonProps: { color: 'error' },
      });
    } catch (cancelled) {
      return;
    }
    if (await actions.deleteCategory(category.id)) {
      onDeleted(category.id);
    }
  };

  const missing = !isNew && !category;

  return (
    <EditorDrawer
      open={open}
      onClose={close}
      title={isNew ? 'New category' : (category && category.name) || 'Category'}
      subtitle={!isNew && category && showIds ? `ID ${category.id}` : null}
      actions={
        !isNew &&
        category && (
          <Tooltip title="Delete category">
            <IconButton aria-label="Delete category" onClick={remove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
      tabs={isNew || missing ? null : TABS}
      tab={tab}
      onTabChange={setTab}
      footer={
        tab === 'details' && !missing ? (
          <>
            <Button color="inherit" onClick={close}>
              {isNew ? 'Cancel' : 'Close'}
            </Button>
            <Button variant="contained" disableElevation onClick={save} disabled={saving || Object.keys(errors).length > 0 || (!isNew && !dirty)}>
              {isNew ? 'Create category' : 'Save changes'}
            </Button>
          </>
        ) : null
      }
    >
      {missing && <Alert severity="info">This category is no longer on the menu.</Alert>}

      {!missing && tab === 'details' && (
        <Stack spacing={2.5}>
          <TextField label="Name" required fullWidth autoFocus={isNew} value={form.name} onChange={set('name')} error={dirty && !!errors.name} helperText={(dirty && errors.name) || ' '} />
          <TextField label="Alternative name" fullWidth value={form.altName} onChange={set('altName')} />
          <TextField label="Description" fullWidth multiline minRows={2} value={form.description} onChange={set('description')} />
          <TextField
            label="Cooking priority"
            type="number"
            value={form.cookingPriority}
            onChange={set('cookingPriority')}
            error={!!errors.cookingPriority}
            helperText={errors.cookingPriority || 'Lower numbers are cooked first'}
            inputProps={{ ...noWheelInputProps, step: 1 }}
            sx={{ maxWidth: 220 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel control={<Switch checked={form.hideForWeb} onChange={toggle('hideForWeb')} />} label="Hide from web ordering" />
            {!isNew && (
              <FormControlLabel control={<Switch checked={form.isHiddenForPickup} onChange={toggle('isHiddenForPickup')} />} label="Hide for pickup" />
            )}
          </Box>
          <ColorField label="Colour" value={form.colorHex} onChange={(colorHex) => setForm({ ...form, colorHex })} helperText="Highlights the category on the POS" />
        </Stack>
      )}

      {!missing && tab === 'visibility' && category && (
        <Box>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Changes on this tab save straight away.
          </Alert>
          <MenuVisibility visibilityObj={category} category={category} />
        </Box>
      )}

      {!missing && tab === 'dishes' && category && (
        <AllDishesTab category={category} menuId={menuId} menu={menu} printCategoryNames={printCategoryNames} actions={actions} />
      )}
    </EditorDrawer>
  );
};

CategoryEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['new', 'edit']),
  category: PropTypes.object,
  menuId: PropTypes.string.isRequired,
  menu: PropTypes.object,
  printCategoryNames: PropTypes.array.isRequired,
  showIds: PropTypes.bool,
  actions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default CategoryEditor;
